import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';

interface AndroidPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AndroidPreviewModal: React.FC<AndroidPreviewModalProps> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const { error: supabaseError } = await supabase
        .from('waitlist')
        .insert([{ email, source: 'website', feature: 'beta-test', platform: 'Android' }]);

      if (supabaseError) {
        if (supabaseError.code === '23505') {
          setSubmitted(true);
          return;
        }
        throw supabaseError;
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting to preview list:', err);
      setError(t('landing.androidPreview.error') as string);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button 
          className="modal-close" 
          onClick={onClose}
          aria-label="Close modal"
        >×</button>
        
        {submitted ? (
          <div className="success-content">
            <h3>{t('landing.androidPreview.success.title')}</h3>
            <p>{t('landing.androidPreview.success.message')}</p>
          </div>
        ) : (
          <>
            <h3 className="modal-title">{t('landing.androidPreview.title')}</h3>
            <p className="modal-description">{t('landing.androidPreview.description')}</p>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {error && (
                <div className="error-message">
                  <p>{error}</p>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="email" className="visually-hidden">
                  {t('landing.androidPreview.emailLabel')}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('landing.androidPreview.emailPlaceholder') as string}
                  required
                  pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                  title={t('summit_submission.form.email.validation') as string}
                />
                <button type="submit" className="submit-button">
                  {t('landing.androidPreview.submit')}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AndroidPreviewModal; 